import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { firestore, storage } from '../../firebase/config';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { uploadUserFile } from '../../firebase/storageHelpers';
import { auth } from '../../firebase/config';
import { updateEmail, updatePassword } from 'firebase/auth';

const TABS = [
  { key: 'company', label: 'Company info' },
  { key: 'account', label: 'Account' },
  { key: 'settings', label: 'Settings' },
];

const ProfileDashboard = () => {
  const { currentUser } = useAuth();
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('company');
  const [editProfile, setEditProfile] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    location: '',
    openToWork: '',
    bio: '',
    photoURL: '',
  });
  const [profileImage, setProfileImage] = useState(null);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const fileInputRef = useRef();
  const [accountForm, setAccountForm] = useState({
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
  });
  const [accountSuccess, setAccountSuccess] = useState('');
  const [accountError, setAccountError] = useState('');
  const [settingsForm, setSettingsForm] = useState({
    newMessages: true,
    subscriptionExpiry: true,
    marketingUpdates: true,
  });
  const [settingsSuccess, setSettingsSuccess] = useState('');
  const [settingsError, setSettingsError] = useState('');

  useEffect(() => {
    const fetchUserProfile = async () => {
      if (!currentUser) return;
      try {
        const profileDoc = await getDoc(doc(firestore, 'userProfiles', currentUser.uid));
        if (profileDoc.exists()) {
          const data = profileDoc.data();
          setUserProfile(data);
          setEditProfile({
            firstName: data.firstName || '',
            lastName: data.lastName || '',
            email: currentUser.email || '',
            phone: data.phone || '',
            location: data.location || '',
            openToWork: data.openToWork || '',
            bio: data.bio || '',
            photoURL: data.photoURL || '',
          });
        } else {
          setEditProfile({
            firstName: '',
            lastName: '',
            email: currentUser.email || '',
            phone: '',
            location: '',
            openToWork: '',
            bio: '',
            photoURL: '',
          });
        }
      } catch (err) {
        setError('Failed to load profile.');
      } finally {
        setLoading(false);
      }
    };
    fetchUserProfile();
  }, [currentUser]);

  // Populate account form when userProfile changes
  useEffect(() => {
    if (userProfile || currentUser) {
      setAccountForm({
        email: currentUser?.email || '',
        phone: userProfile?.phone || '',
        password: '',
        confirmPassword: '',
      });
    }
  }, [userProfile, currentUser]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditProfile((prev) => ({ ...prev, [name]: value }));
  };

  const handleImageChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setProfileImage(e.target.files[0]);
      setEditProfile((prev) => ({ ...prev, photoURL: URL.createObjectURL(e.target.files[0]) }));
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      let photoURL = editProfile.photoURL;
      // If a new image is selected, upload it
      if (profileImage) {
        try {
          photoURL = await uploadUserFile('userProfilePhoto', currentUser.uid, profileImage, 'profile');
        } catch (uploadErr) {
          setError('Failed to upload profile image.');
          setSaving(false);
          return;
        }
      }
      // Save profile data to Firestore, including the new photoURL if uploaded
      await setDoc(doc(firestore, 'userProfiles', currentUser.uid), {
        ...userProfile,
        ...editProfile,
        photoURL,
      }, { merge: true });
      setEditProfile((prev) => ({ ...prev, photoURL }));
      setSuccess('Profile updated successfully!');
      setProfileImage(null); // Reset after upload
    } catch (err) {
      setError('Failed to save profile.');
    } finally {
      setSaving(false);
    }
  };

  const handleAccountInputChange = (e) => {
    const { name, value } = e.target;
    setAccountForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleAccountCancel = () => {
    setAccountForm({
      email: currentUser?.email || '',
      phone: userProfile?.phone || '',
      password: '',
      confirmPassword: '',
    });
    setAccountError('');
    setAccountSuccess('');
  };

  const handleAccountSave = async (e) => {
    e.preventDefault();
    setAccountError('');
    setAccountSuccess('');
    if (accountForm.password !== accountForm.confirmPassword) {
      setAccountError('Passwords do not match.');
      return;
    }
    try {
      // Update email if changed
      if (accountForm.email !== currentUser.email) {
        await updateEmail(auth.currentUser, accountForm.email);
        // Also update in Firestore
        await setDoc(doc(firestore, 'userProfiles', currentUser.uid), {
          email: accountForm.email,
        }, { merge: true });
      }
      // Update password if provided
      if (accountForm.password) {
        await updatePassword(auth.currentUser, accountForm.password);
      }
      // Update phone in Firestore if changed
      if (accountForm.phone !== userProfile?.phone) {
        await setDoc(doc(firestore, 'userProfiles', currentUser.uid), {
          phone: accountForm.phone,
        }, { merge: true });
      }
      setAccountSuccess('Account changes saved successfully!');
    } catch (err) {
      if (err.code === 'auth/requires-recent-login') {
        setAccountError('For security, please log out and log in again before changing your email or password.');
      } else {
        setAccountError('Failed to update account: ' + (err.message || 'Unknown error'));
      }
    }
  };

  const handleSettingsToggle = (e) => {
    const { name, checked } = e.target;
    setSettingsForm((prev) => ({ ...prev, [name]: checked }));
  };

  const handleSettingsCancel = () => {
    setSettingsForm({
      newMessages: true,
      subscriptionExpiry: true,
      marketingUpdates: true,
    });
    setSettingsSuccess('');
    setSettingsError('');
  };

  const handleSettingsSave = (e) => {
    e.preventDefault();
    setSettingsSuccess('Preferences saved (not yet implemented).');
    setSettingsError('');
  };

  if (loading) {
    return (
      <div className="p-3 sm:p-6 flex justify-center items-center h-[50vh] sm:h-[70vh]">
        <div className="animate-spin rounded-full h-8 w-8 sm:h-12 sm:w-12 border-t-2 border-b-2 border-[#FF8C00]"></div>
      </div>
    );
  }

  return (
    <div className="p-3 sm:p-6 max-w-6xl mx-auto">
      <div className="mb-4 sm:mb-6">
        <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold mb-1 text-left">Your Profile</h1>
        <p className="text-sm sm:text-base text-gray-500 text-left">Manage your personal information and job preferences</p>
      </div>
      
      {/* Tabs - Responsive with scrolling on mobile */}
      <div className="mb-4 sm:mb-6 overflow-x-auto">
        <div className="flex gap-1 sm:gap-2 min-w-max sm:min-w-0">
          {TABS.map((tab) => (
            <button
              key={tab.key}
              className={`px-3 sm:px-6 py-2 rounded-t-md font-medium border-b-2 transition-colors whitespace-nowrap text-sm sm:text-base ${
                activeTab === tab.key 
                  ? 'border-[#FF8C00] bg-white text-[#FF8C00]' 
                  : 'border-transparent bg-gray-100 text-gray-600 hover:text-[#FF8C00]'
              }`}
              onClick={() => setActiveTab(tab.key)}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>
      
      {/* Tab Content */}
      {activeTab === 'company' && (
        <form className="bg-white rounded-lg shadow p-3 sm:p-6" onSubmit={handleSave}>
          <div className="mb-4 sm:mb-6">
            <h2 className="text-lg sm:text-xl font-bold mb-1 text-left">Personal Information</h2>
            <p className="text-sm sm:text-base text-gray-500 text-left">Manage your company information and account settings.</p>
          </div>
          
          {/* Profile Image Section - Responsive */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center mb-4 sm:mb-6 gap-3 sm:gap-4">
            <div className="relative w-16 h-16 sm:w-20 sm:h-20 mx-auto sm:mx-0">
              <img
                src={editProfile.photoURL || '/asset/default-avatar.png'}
                alt="Profile"
                className="w-16 h-16 sm:w-20 sm:h-20 rounded-full object-cover border"
              />
              <button
                type="button"
                className="absolute bottom-0 right-0 bg-[#FF8C00] text-white rounded-full p-1 border border-white shadow hover:bg-[#FF7700] transition-colors"
                onClick={() => fileInputRef.current.click()}
                tabIndex={-1}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 sm:h-4 sm:w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536M9 13h3l8-8a2.828 2.828 0 10-4-4l-8 8v3zm0 0v3h3" />
                </svg>
              </button>
              <input
                type="file"
                accept="image/*"
                className="hidden"
                ref={fileInputRef}
                onChange={handleImageChange}
              />
            </div>
            <div className="text-center sm:text-left">
              <div className="font-bold text-base sm:text-lg">
                {editProfile.firstName || 'First name'} {editProfile.lastName || 'Last name'}
              </div>
              <div className="text-gray-500 text-xs sm:text-sm">
                Your basic information visible to employers
              </div>
            </div>
          </div>
          
          {/* Form Fields - Responsive Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mb-4">
            <div>
              <label className="block text-xs sm:text-sm font-medium mb-1 text-left">First name</label>
              <input 
                type="text" 
                name="firstName" 
                value={editProfile.firstName} 
                onChange={handleInputChange} 
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm sm:text-base focus:outline-none focus:ring-2 focus:ring-[#FF8C00] focus:border-transparent" 
                placeholder="Enter your first name"
              />
            </div>
            <div>
              <label className="block text-xs sm:text-sm font-medium mb-1 text-left">Last name</label>
              <input 
                type="text" 
                name="lastName" 
                value={editProfile.lastName} 
                onChange={handleInputChange} 
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm sm:text-base focus:outline-none focus:ring-2 focus:ring-[#FF8C00] focus:border-transparent" 
                placeholder="Enter your last name"
              />
            </div>
            <div>
              <label className="block text-xs sm:text-sm font-medium mb-1 text-left">Email</label>
              <input 
                type="email" 
                name="email" 
                value={editProfile.email} 
                disabled 
                className="w-full border border-gray-200 rounded-md px-3 py-2 bg-gray-100 cursor-not-allowed text-sm sm:text-base text-gray-600" 
              />
            </div>
            <div>
              <label className="block text-xs sm:text-sm font-medium mb-1 text-left">Phone</label>
              <input 
                type="text" 
                name="phone" 
                value={editProfile.phone} 
                onChange={handleInputChange} 
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm sm:text-base focus:outline-none focus:ring-2 focus:ring-[#FF8C00] focus:border-transparent" 
                placeholder="Enter your phone number"
              />
            </div>
            <div>
              <label className="block text-xs sm:text-sm font-medium mb-1 text-left">Location</label>
              <input 
                type="text" 
                name="location" 
                value={editProfile.location} 
                onChange={handleInputChange} 
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm sm:text-base focus:outline-none focus:ring-2 focus:ring-[#FF8C00] focus:border-transparent" 
                placeholder="City, State"
              />
            </div>
            <div>
              <label className="block text-xs sm:text-sm font-medium mb-1 text-left">Open to work</label>
              <select 
                name="openToWork" 
                value={editProfile.openToWork} 
                onChange={handleInputChange} 
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm sm:text-base focus:outline-none focus:ring-2 focus:ring-[#FF8C00] focus:border-transparent"
              >
                <option value="">Select...</option>
                <option value="yes">Yes</option>
                <option value="no">No</option>
              </select>
            </div>
          </div>
          
          {/* Bio Section */}
          <div className="mb-4 sm:mb-6">
            <label className="block text-xs sm:text-sm font-medium mb-1 text-left">Bio</label>
            <textarea 
              name="bio" 
              value={editProfile.bio} 
              onChange={handleInputChange} 
              className="w-full border border-gray-300 rounded-md px-3 py-2 min-h-[80px] sm:min-h-[100px] text-sm sm:text-base focus:outline-none focus:ring-2 focus:ring-[#FF8C00] focus:border-transparent" 
              placeholder="Tell employers about yourself..."
            />
          </div>
          
          {/* Messages */}
          {error && (
            <div className="text-red-600 mb-2 text-sm sm:text-base p-3 bg-red-50 border border-red-200 rounded-md">
              {error}
            </div>
          )}
          {success && (
            <div className="text-green-600 mb-2 text-sm sm:text-base p-3 bg-green-50 border border-green-200 rounded-md">
              {success}
            </div>
          )}
          
          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
            <button 
              type="submit" 
              className="w-full sm:w-auto bg-[#FF8C00] text-white px-4 sm:px-6 py-2 sm:py-3 rounded-md font-medium hover:bg-[#FF7700] transition-colors text-sm sm:text-base disabled:opacity-50 disabled:cursor-not-allowed" 
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      )}
      
      {activeTab === 'account' && (
        <form className="bg-white rounded-lg shadow p-3 sm:p-6" onSubmit={handleAccountSave}>
          <div className="mb-4 sm:mb-6">
            <h2 className="text-lg sm:text-xl font-bold mb-1 text-left">Account Manager</h2>
            <p className="text-sm sm:text-base text-gray-500 text-left">Manage your account credentials and security settings</p>
          </div>
          
          <div className="font-semibold mb-3 mt-2 text-sm sm:text-base text-left">Login information</div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mb-4">
            <div>
              <label className="block text-xs sm:text-sm font-medium mb-1 text-left">Login email</label>
              <input 
                type="email" 
                name="email" 
                value={accountForm.email} 
                onChange={handleAccountInputChange} 
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm sm:text-base focus:outline-none focus:ring-2 focus:ring-[#FF8C00] focus:border-transparent" 
              />
            </div>
            <div>
              <label className="block text-xs sm:text-sm font-medium mb-1 text-left">Phone</label>
              <input 
                type="text" 
                name="phone" 
                value={accountForm.phone} 
                onChange={handleAccountInputChange} 
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm sm:text-base focus:outline-none focus:ring-2 focus:ring-[#FF8C00] focus:border-transparent" 
              />
            </div>
            <div>
              <label className="block text-xs sm:text-sm font-medium mb-1 text-left">New password</label>
              <input 
                type="password" 
                name="password" 
                value={accountForm.password} 
                onChange={handleAccountInputChange} 
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm sm:text-base focus:outline-none focus:ring-2 focus:ring-[#FF8C00] focus:border-transparent" 
                placeholder="Leave blank to keep current password"
              />
            </div>
            <div>
              <label className="block text-xs sm:text-sm font-medium mb-1 text-left">Confirm password</label>
              <input 
                type="password" 
                name="confirmPassword" 
                value={accountForm.confirmPassword} 
                onChange={handleAccountInputChange} 
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm sm:text-base focus:outline-none focus:ring-2 focus:ring-[#FF8C00] focus:border-transparent" 
                placeholder="Confirm new password"
              />
            </div>
          </div>
          
          {/* Messages */}
          {accountError && (
            <div className="text-red-600 mb-2 text-sm sm:text-base p-3 bg-red-50 border border-red-200 rounded-md">
              {accountError}
            </div>
          )}
          {accountSuccess && (
            <div className="text-green-600 mb-2 text-sm sm:text-base p-3 bg-green-50 border border-green-200 rounded-md">
              {accountSuccess}
            </div>
          )}
          
          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 mt-4">
            <button 
              type="button" 
              className="w-full sm:w-auto bg-gray-200 text-gray-700 px-4 sm:px-6 py-2 sm:py-3 rounded-md font-medium hover:bg-gray-300 transition-colors text-sm sm:text-base" 
              onClick={handleAccountCancel}
            >
              Cancel
            </button>
            <button 
              type="submit" 
              className="w-full sm:w-auto bg-[#FF8C00] text-white px-4 sm:px-6 py-2 sm:py-3 rounded-md font-medium hover:bg-[#FF7700] transition-colors text-sm sm:text-base"
            >
              Save changes
            </button>
          </div>
        </form>
      )}
      
      {activeTab === 'settings' && (
        <form className="bg-white rounded-lg shadow p-3 sm:p-6" onSubmit={handleSettingsSave}>
          <div className="mb-4 sm:mb-6">
            <h2 className="text-lg sm:text-xl font-bold mb-1 text-left">Notification Preferences</h2>
            <p className="text-sm sm:text-base text-gray-500 text-left">Manage how and when you receive notifications</p>
          </div>
          
          <div className="font-semibold mb-3 mt-2 text-sm sm:text-base text-left">Email notifications</div>
          
          <div className="space-y-6 sm:space-y-8 mb-6">
            {/* New messages */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
              <div className="text-left">
                <div className="font-medium text-sm sm:text-base">New messages</div>
                <div className="text-gray-500 text-xs sm:text-sm">Receive an email when you get a new message from a candidate</div>
              </div>
              <label className="relative inline-flex items-center cursor-pointer flex-shrink-0">
                <input type="checkbox" name="newMessages" checked={settingsForm.newMessages} onChange={handleSettingsToggle} className="sr-only peer" />
                <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:bg-blue-600 transition-colors"></div>
                <div className="absolute left-0.5 top-0.5 w-5 h-5 bg-white border border-gray-300 rounded-full transition-transform duration-200 peer-checked:translate-x-5"></div>
              </label>
            </div>
            
            {/* Subscription expiry updates */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
              <div className="text-left">
                <div className="font-medium text-sm sm:text-base">Subscription expiry updates</div>
                <div className="text-gray-500 text-xs sm:text-sm">Receive an email when your subscription or promotions is expiring</div>
              </div>
              <label className="relative inline-flex items-center cursor-pointer flex-shrink-0">
                <input type="checkbox" name="subscriptionExpiry" checked={settingsForm.subscriptionExpiry} onChange={handleSettingsToggle} className="sr-only peer" />
                <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:bg-blue-600 transition-colors"></div>
                <div className="absolute left-0.5 top-0.5 w-5 h-5 bg-white border border-gray-300 rounded-full transition-transform duration-200 peer-checked:translate-x-5"></div>
              </label>
            </div>
            
            {/* Marketing & Updates */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
              <div className="text-left">
                <div className="font-medium text-sm sm:text-base">Marketing & Updates</div>
                <div className="text-gray-500 text-xs sm:text-sm">Receive updates about new features and promotions</div>
              </div>
              <label className="relative inline-flex items-center cursor-pointer flex-shrink-0">
                <input type="checkbox" name="marketingUpdates" checked={settingsForm.marketingUpdates} onChange={handleSettingsToggle} className="sr-only peer" />
                <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:bg-blue-600 transition-colors"></div>
                <div className="absolute left-0.5 top-0.5 w-5 h-5 bg-white border border-gray-300 rounded-full transition-transform duration-200 peer-checked:translate-x-5"></div>
              </label>
            </div>
          </div>
          
          {/* Messages */}
          {settingsError && (
            <div className="text-red-600 mb-2 text-sm sm:text-base p-3 bg-red-50 border border-red-200 rounded-md">
              {settingsError}
            </div>
          )}
          {settingsSuccess && (
            <div className="text-green-600 mb-2 text-sm sm:text-base p-3 bg-green-50 border border-red-200 rounded-md">
              {settingsSuccess}
            </div>
          )}
          
          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 mt-4">
            <button 
              type="button" 
              className="w-full sm:w-auto bg-gray-200 text-gray-700 px-4 sm:px-6 py-2 sm:py-3 rounded-md font-medium hover:bg-gray-300 transition-colors text-sm sm:text-base" 
              onClick={handleSettingsCancel}
            >
              Cancel
            </button>
            <button 
              type="submit" 
              className="w-full sm:w-auto bg-[#FF8C00] text-white px-4 sm:px-6 py-2 sm:py-3 rounded-md font-medium hover:bg-[#FF7700] transition-colors text-sm sm:text-base"
            >
              Save changes
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

export default ProfileDashboard; 